"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Client } from "@/lib/types/database";
import type { User } from "@/lib/types/user";
import { EmailRecipient } from "../ClientEmailSelector";
import { Clock, Loader2, Mail } from "lucide-react";
import { isValidEmail } from "../utils/email-validation";
import { ContactOptionPreview } from "@/components/ui/selects/components/ContactOptionPreview";
import { getRecents } from "@/components/ui/responsive-select/recents";

/**
 * Module-scope empties: a `= []` default allocates a NEW array on every render,
 * which invalidates the `recentClients` memo below every single time the prop is
 * omitted (MEMO_STABLE_PROPS).
 */
const EMPTY_CLIENTS: Client[] = [];
const EMPTY_HUB_USERS: User[] = [];

interface RecipientDropdownProps {
  inputValue: string;
  allClients?: Client[];
  filteredClients: Client[];
  clientsLoading?: boolean;
  isSearching?: boolean;
  filteredHubUsers?: User[];
  hubUsersLoading?: boolean;
  selectedRecipients: EmailRecipient[];
  onAddClient: (client: Client) => void;
  onAddHubUser?: (user: User) => void;
  onAddEmail: (email: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function RecipientDropdown({
  inputValue,
  allClients = EMPTY_CLIENTS,
  filteredClients,
  clientsLoading = false,
  isSearching = false,
  filteredHubUsers = EMPTY_HUB_USERS,
  hubUsersLoading = false,
  selectedRecipients,
  onAddClient,
  onAddHubUser,
  onAddEmail,
  inputRef,
}: RecipientDropdownProps) {
  const recentClients = React.useMemo(() => {
    const recentIds = getRecents("contacts").map((item) => item.id);
    if (recentIds.length === 0) return [];

    const source = [...allClients, ...filteredClients].filter(
      (client, index, arr) => arr.findIndex((c) => c.id === client.id) === index
    );
    const selectedEmails = new Set(selectedRecipients.map((r) => r.email));
    const search = inputValue.trim().toLowerCase();

    return recentIds
      .map((id) => source.find((client) => client.id === id))
      .filter((client): client is Client => Boolean(client?.email))
      .filter((client, index, arr) => arr.findIndex((c) => c.id === client.id) === index)
      .filter((client) => !selectedEmails.has(client.email!))
      .filter((client) => {
        if (!search) return true;
        return (
          client.name?.toLowerCase().includes(search) ||
          client.company_name?.toLowerCase().includes(search) ||
          client.email?.toLowerCase().includes(search)
        );
      })
      .slice(0, 5);
  }, [allClients, filteredClients, selectedRecipients, inputValue]);

  return (
    <div
      className="w-full max-w-none"
      data-popover-content
      onMouseDown={(e) => {
        // Prevent focus from moving to popover when clicking inside it
        e.preventDefault();
      }}
      tabIndex={-1}
    >
      <div className="space-y-2">
        {(clientsLoading || isSearching) && (
          <div className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground border-b">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>{inputValue ? "Searching contacts..." : "Loading contacts..."}</span>
          </div>
        )}

        {/* Show what we're searching for (read-only) */}
        {inputValue && (
          <div className="text-xs text-muted-foreground px-2 py-1 border-b">
            Searching for: &quot;{inputValue}&quot;
          </div>
        )}

        {/* Recent contact results */}
        {recentClients.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground px-2 py-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Recent Contacts
            </div>
            {recentClients.map((client) => (
              <button
                key={`recent-${client.id}`}
                type="button"
                tabIndex={-1}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAddClient(client);
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
                className="w-full flex items-center gap-2 p-2 text-left hover:bg-accent rounded-sm transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <ContactOptionPreview
                    label={client.name || client.company_name || client.email || "Unknown contact"}
                    status={client.status ?? undefined}
                    companyName={client.company_name ?? undefined}
                    title={client.title ?? undefined}
                    email={client.email ?? undefined}
                  />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Client results */}
        {filteredClients.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground px-2 py-1">Clients</div>
            {filteredClients.map((client) => (
              <button
                key={client.id}
                type="button"
                tabIndex={-1}
                onMouseDown={(e) => {
                  // Prevent focus change when clicking
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAddClient(client);
                  // Return focus to input after selection
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
                className="w-full flex items-center gap-2 p-2 text-left hover:bg-accent rounded-sm transition-colors"
                disabled={!client.email || selectedRecipients.some((r) => r.email === client.email)}
              >
                <div className="flex-1 min-w-0">
                  <ContactOptionPreview
                    label={client.name || client.company_name || client.email || "Unknown contact"}
                    status={client.status ?? undefined}
                    companyName={client.company_name ?? undefined}
                    title={client.title ?? undefined}
                    email={client.email ?? undefined}
                  />
                </div>
                {selectedRecipients.some((r) => r.email === client.email) && (
                  <Badge variant="secondary" className="text-xs">
                    Added
                  </Badge>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Hub User results */}
        {onAddHubUser && (filteredHubUsers.length > 0 || hubUsersLoading) && (
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground px-2 py-1">Hub Users</div>
            {hubUsersLoading ? (
              <div className="p-2 text-xs text-muted-foreground">Searching hub users...</div>
            ) : (
              filteredHubUsers.map((user) => {
                const displayName =
                  [user.first_name, user.last_name].filter(Boolean).join(" ") ||
                  user.email ||
                  "Unknown user";
                return (
                  <button
                    key={user.id}
                    type="button"
                    tabIndex={-1}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAddHubUser(user);
                      if (inputRef.current) inputRef.current.focus();
                    }}
                    className="w-full flex items-center gap-2 p-2 text-left hover:bg-accent rounded-sm transition-colors"
                    disabled={!user.email || selectedRecipients.some((r) => r.email === user.email)}
                  >
                    <div className="flex-1 min-w-0">
                      <ContactOptionPreview
                        label={displayName}
                        status={typeof user.role === "string" ? user.role : undefined}
                        email={user.email ?? undefined}
                      />
                    </div>
                    {selectedRecipients.some((r) => r.email === user.email) && (
                      <Badge variant="secondary" className="text-xs">
                        Added
                      </Badge>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}

        {/* Add email option */}
        {inputValue &&
          isValidEmail(inputValue) &&
          !selectedRecipients.some((r) => r.email === inputValue) && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                Email Address
              </div>
              <button
                type="button"
                tabIndex={-1}
                onMouseDown={(e) => {
                  // Prevent focus change when clicking
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAddEmail(inputValue);
                  // Return focus to input after selection
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
                className="w-full flex items-center gap-2 p-2 text-left hover:bg-accent rounded-sm transition-colors"
              >
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="text-sm">Add &quot;{inputValue}&quot;</div>
                </div>
              </button>
            </div>
          )}

        {/* No results */}
        {inputValue &&
          filteredClients.length === 0 &&
          filteredHubUsers.length === 0 &&
          !isSearching &&
          !clientsLoading &&
          !hubUsersLoading &&
          !isValidEmail(inputValue) && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No clients or hub users found. Try entering an email address.
            </div>
          )}
      </div>
    </div>
  );
}
